import { INITIATIVE_FIELDS, normalizeField } from "../domain/initiativeFields";

/*
 * Minimal RFC4180 CSV reader — the counterpart to the hand-rolled writer in
 * exportUtils.js. Handles quoted fields, embedded commas/newlines, and ""
 * escaped quotes without pulling in a parsing dependency.
 */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i];
    if (inQuotes) {
      if (char === '"') {
        if (normalized[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      pushField();
    } else if (char === "\n") {
      pushRow();
    } else {
      field += char;
    }
  }
  if (field !== "" || row.length > 0) pushRow();

  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

/*
 * Header <-> field mapping, in the order exportToCsv writes them.
 * OPTIONAL_HEADERS may be blank or omitted from the file entirely.
 */
const COLUMN_FIELDS = [
  ["Title", "title"],
  ["Horizon", "horizon"],
  ["Status", "status"],
  ["Priority", "priority"],
  ["Target Period", "targetPeriod"],
  ["Products", "product"],
  ["Impact Areas", "impactAreas"],
  ["Owner", "owner"],
  ["Progress", "progress"],
  ["Description", "description"],
  ["Estimate", "estimate"],
  ["Gitlab Epic", "gitlabEpic"],
  ["Design Link", "designLink"],
];
const OPTIONAL_HEADERS = new Set([
  "Target Period",
  "Impact Areas",
  "Gitlab Epic",
  "Design Link",
]);
const LOOKUP_TABLES_BY_FIELD = {
  horizon: "horizons",
  status: "statuses",
  priority: "priorities",
  product: "products",
  impactAreas: "impactAreas",
};

const HEADER_TO_FIELD = new Map(COLUMN_FIELDS);
const REQUIRED_HEADERS = COLUMN_FIELDS.map(([header]) => header).filter(
  (header) => !OPTIONAL_HEADERS.has(header),
);

// Mirrors the wording of server/api/src/validation/initiatives.js's FIELD_RULES.
const FIELD_ERROR_MESSAGES = {
  title: "title is required and must be 1-100 characters",
  description: "description is required and must be 1-1000 characters",
  owner: "owner is required and must be 1-100 characters",
  horizon: "horizon is required",
  status: "status is required",
  priority: "priority is required",
  estimate: "estimate is required and must be a number between 1 and 4",
  progress: "progress is required and must be a number between 0 and 100",
  product: "product is required and must be a non-empty array of strings",
  targetPeriod: 'targetPeriod must be in the format "YYYY QN" (e.g. "2026 Q3")',
};

/*
 * A missing required column is fatal — without it there's no way to locate
 * that field's value in any row, so there's nothing useful to check further.
 * An unrecognized column is not fatal: it's reported once (row 1) but parsing
 * still proceeds through every data row, so a stray/misspelled extra column
 * doesn't stop the rest of the file's problems from being surfaced too.
 */
function findMissingRequiredHeaders(headerRow) {
  return REQUIRED_HEADERS.filter((h) => !headerRow.includes(h));
}

function findUnrecognizedHeaders(headerRow) {
  return headerRow.filter((h) => !HEADER_TO_FIELD.has(h));
}

function buildRawRow(headerRow, cells) {
  const raw = {};
  headerRow.forEach((header, colIndex) => {
    const field = HEADER_TO_FIELD.get(header);
    if (!field) return;
    raw[field] = (cells[colIndex] ?? "").trim();
  });
  return raw;
}

function coerceField(field, rawValue) {
  const { type } = INITIATIVE_FIELDS[field];
  if (type === "stringArray") {
    return normalizeField(field, rawValue === "" ? [] : rawValue.split("|"));
  }
  return normalizeField(field, rawValue);
}

const isBlankValue = (value) =>
  value === "" || (Array.isArray(value) && value.length === 0);

/*
 * Validates and builds one row's create-payload. Reuses INITIATIVE_FIELDS'
 * own validators (src/domain/initiativeFields.js) for required/format rules —
 * the same rules the create form applies — then separately checks that
 * horizon/status/priority/product/impactAreas values are recognized lookup
 * names, since INITIATIVE_FIELDS only checks shape, not real-world validity.
 */
function parseRow(headerRow, cells, rowNumber, lookups) {
  const raw = buildRawRow(headerRow, cells);
  const item = {};
  const erroredFields = new Set();
  const errors = [];

  for (const [, field] of COLUMN_FIELDS) {
    if (!(field in raw)) continue;
    item[field] = coerceField(field, raw[field]);
  }

  for (const [field, config] of Object.entries(INITIATIVE_FIELDS)) {
    if (!(field in item)) continue;
    const value = item[field];

    if (!config.required && isBlankValue(value)) {
      delete item[field];
      continue;
    }
    if (config.validate && !config.validate(value)) {
      erroredFields.add(field);
      errors.push({
        row: rowNumber,
        field,
        message: FIELD_ERROR_MESSAGES[field] || `${field} is invalid`,
      });
    }
  }

  for (const [field, table] of Object.entries(LOOKUP_TABLES_BY_FIELD)) {
    if (!(field in item) || erroredFields.has(field)) continue;
    const validNames = new Set(
      (lookups[table] || []).map((entry) => entry.name),
    );
    const values = Array.isArray(item[field]) ? item[field] : [item[field]];
    const unrecognized = values.filter((v) => !validNames.has(v));
    if (unrecognized.length > 0) {
      errors.push({
        row: rowNumber,
        field,
        message: `${field} contains unrecognized value(s): ${unrecognized.join(", ")}`,
      });
    }
  }

  return { item, errors };
}

/*
 * Parses a CSV file's text into create-ready payloads plus every validation
 * error found. A row with errors doesn't stop the parse — it's noted and
 * skipped so the rest of the file still gets checked, meaning `errors` ends
 * up holding every problem in the file at once rather than just the first.
 * Import is all-or-nothing: the caller should only use `items` when `errors`
 * is empty, even though `items` itself only ever contains well-formed rows.
 */
export function parseImportFile(text, lookups) {
  const rows = parseCsv(text);
  if (rows.length === 0) {
    return {
      items: [],
      errors: [{ row: 1, field: null, message: "The file is empty." }],
    };
  }

  const [headerRow, ...dataRows] = rows;
  const missingRequired = findMissingRequiredHeaders(headerRow);
  if (missingRequired.length > 0) {
    return {
      items: [],
      errors: [
        {
          row: 1,
          field: null,
          message: `Missing required column(s): ${missingRequired.join(", ")}.`,
        },
      ],
    };
  }

  const items = [];
  const errors = [];
  const unrecognized = findUnrecognizedHeaders(headerRow);
  if (unrecognized.length > 0) {
    errors.push({
      row: 1,
      field: null,
      message: `Unrecognized column(s): ${unrecognized.join(", ")}.`,
    });
  }

  dataRows.forEach((cells, index) => {
    const { item, errors: rowErrors } = parseRow(
      headerRow,
      cells,
      index + 2,
      lookups,
    );
    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
    } else {
      items.push(item);
    }
  });

  return { items, errors };
}
